import {
  BudgetLineItem,
  BudgetTransferLine,
  CashflowMonthlyLine,
  PcgContingencyHistoryPoint,
  VariationLineItem,
} from '../models'
import {
  buildCurrentStatusAmountTotalsByKey,
  isRejectedOrWithdrawnPcgVariationRow,
  isRejectedOrWithdrawnPcgVariationStatus,
  normalizePcgVariationStatusKey,
  PcgVariationStatusItem,
} from './variationStatusCalculations'

type PcgGroup = 'mainWorks' | 'variations' | 'contingency' | 'other'

type BudgetTreeItem = BudgetLineItem & {
  pcgGroup?: PcgGroup
  children?: BudgetTreeItem[]
}

type BudgetTotals = {
  initialBudget: number
  budgetTransfers: number
  adjustedBudget: number
  anticipated: number
  endCost: number
  variance: number
  committed: number
  nonCommitted: number
  expenditure: number
  costToComplete: number
}

export type ContingencyErosionValueType = 'currency' | 'percentage'

export type ContingencyErosionMonth = {
  key: string
  label: string
  date: Date
}

export type ContingencyErosionTableRow = {
  id: string
  metric: string
  valueType: ContingencyErosionValueType
  values: Array<number | null>
  children?: ContingencyErosionTableRow[]
}

export type ContingencyErosionSeriesKey =
  | 'cumulativeForecastExpenditure'
  | 'forecastContingencyErosion'
  | 'actualCumulativeExpenditure'
  | 'actualCostToComplete'
  | 'forecastCostToComplete'
  | 'actualContingencyCostToComplete'
  | 'remainingContingency'
  | 'contingencyForecastPercent'
  | 'contingencyForecastFivePercent'

export type ContingencyErosionSeries = {
  key: ContingencyErosionSeriesKey
  label: string
  values: Array<number | null>
}

export type ContingencyErosionPercentageRow = {
  monthKey: string
  label: string
  value: number | null
}

export type ContingencyErosionData = {
  months: ContingencyErosionMonth[]
  tableRows: ContingencyErosionTableRow[]
  remainingContingencyPercentages: ContingencyErosionPercentageRow[]
  expenditureVsContingencySeries: ContingencyErosionSeries[]
  contingencySeries: ContingencyErosionSeries[]
  totals: {
    mainWorksAdjustedBudget: number
    mainWorksActualBaseCostToComplete: number
    contingencyAdjustedBudget: number
    contingencyNonCommitted: number
    contingencyForecastPercentage: number
    fixedContingencyPercentage: number
  }
}

type BuildContingencyErosionDataOptions = {
  budget: BudgetLineItem[]
  budgetTransfers?: BudgetTransferLine[]
  variations?: VariationLineItem[]
  variationStatusItems?: PcgVariationStatusItem[]
  monthly: CashflowMonthlyLine[]
  contingencyHistory?: PcgContingencyHistoryPoint[]
  constructionStartDate?: string | null
  constructionCompletionDate?: string | null
  reportDateOfIssue?: string | null
}

const MONTH_LABELS = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
]
const MONTH_LABEL_TO_INDEX = MONTH_LABELS.reduce<Record<string, number>>((acc, label, idx) => {
  acc[label] = idx
  return acc
}, {})

const PCG_GROUP_PATTERNS: Record<PcgGroup, RegExp> = {
  mainWorks: /main\s*works?/i,
  variations: /variation/i,
  contingency: /contingency/i,
  other: /other\s*costs?/i,
}

const NUMERIC_FIELDS: Array<keyof BudgetTotals> = [
  'initialBudget',
  'budgetTransfers',
  'adjustedBudget',
  'anticipated',
  'endCost',
  'variance',
  'committed',
  'nonCommitted',
  'expenditure',
  'costToComplete',
]

const toMonthLabel = (date: Date) => {
  const month = MONTH_LABELS[date.getMonth()] ?? ''
  const year = date.getFullYear().toString().slice(-2)
  return `${month}-${year}`
}

export const parseContingencyMonthValue = (value?: string | null): Date | null => {
  if (!value) return null
  const trimmed = value.trim()
  if (!trimmed) return null

  const isoMonthMatch = trimmed.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?$/)
  if (isoMonthMatch) {
    const [, yearStr, monthStr] = isoMonthMatch
    const year = Number(yearStr)
    const monthIndex = Number(monthStr) - 1
    if (!Number.isNaN(year) && monthIndex >= 0 && monthIndex < 12) {
      return new Date(year, monthIndex, 1)
    }
  }

  const abbrMatch = trimmed.toUpperCase().match(/^([A-Z]{3})[-\s/](\d{2,4})$/)
  if (abbrMatch) {
    const [, abbr, yearPart] = abbrMatch
    const monthIndex = MONTH_LABEL_TO_INDEX[abbr]
    if (monthIndex !== undefined) {
      const year = yearPart.length === 2 ? Number(`20${yearPart}`) : Number(yearPart)
      if (!Number.isNaN(year)) {
        return new Date(year, monthIndex, 1)
      }
    }
  }

  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.valueOf())) return null
  return new Date(parsed.getFullYear(), parsed.getMonth(), 1)
}

const normalizeMonthLabel = (value?: string | null) => {
  const parsed = parseContingencyMonthValue(value)
  if (parsed) {
    return toMonthLabel(parsed)
  }
  return (value ?? '').trim().toUpperCase()
}

const addMonths = (date: Date, count: number) =>
  new Date(date.getFullYear(), date.getMonth() + count, 1)

const toNumber = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0

const roundCurrency = (value: number) => Number(value.toFixed(2))

const roundPercentage = (value: number | null) =>
  value === null || !Number.isFinite(value) ? null : Number(value.toFixed(2))

const formatPercentageLabel = (value: number) => {
  if (!Number.isFinite(value)) {
    return '0'
  }
  return Number(value.toFixed(2)).toString()
}

const clampChartPercentage = (value: number | null) => {
  if (value === null || !Number.isFinite(value)) {
    return null
  }
  return Math.min(100, Math.max(0, value))
}

const dividePercentage = (numerator: number, denominator: number) => {
  if (!Number.isFinite(denominator) || denominator === 0) {
    return null
  }
  const value = (numerator / denominator) * 100
  return Number.isFinite(value) ? value : null
}

const resolvePcgGroup = (title?: string): PcgGroup | null => {
  if (!title) return null
  if (PCG_GROUP_PATTERNS.other.test(title)) return 'other'
  if (PCG_GROUP_PATTERNS.variations.test(title)) return 'variations'
  if (PCG_GROUP_PATTERNS.contingency.test(title)) return 'contingency'
  if (PCG_GROUP_PATTERNS.mainWorks.test(title)) return 'mainWorks'
  return null
}

const isSectionHeader = (item?: BudgetLineItem) =>
  item?.section === 'header' || Boolean(item?.children?.length)
const isSummaryRow = (item?: BudgetLineItem) =>
  item?.section === 'summary' || Boolean(item?.title && item.title.toLowerCase().includes('total'))
const isTotalRow = (item?: BudgetLineItem) => Boolean(item?.isTotalRow)
const isEditableRow = (item: BudgetLineItem) =>
  !isSectionHeader(item) && !isSummaryRow(item) && !isTotalRow(item)

const normalizeTransferKey = (value?: string | null) => (value ?? '').trim().toLowerCase()
const normalizeFundingSourceKey = (value?: string | null) =>
  (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const getFundingSourceTokens = (value?: string | null) =>
  normalizeFundingSourceKey(value)
    .split(' ')
    .filter((token) => token && token !== 'contingency')

const normalizeStatusToken = (value?: string | null) =>
  (value ?? '')
    .replace(/[^a-z0-9]+/gi, '')
    .trim()
    .toLowerCase()

const buildBudgetTransferTotals = (rows: BudgetTransferLine[] = []): Map<string, number> => {
  const totals = new Map<string, number>()
  const accumulate = (key: string, delta: number) => {
    if (!key) return
    totals.set(key, roundCurrency((totals.get(key) ?? 0) + delta))
  }

  const visit = (row: BudgetTransferLine) => {
    if (row.isTotalRow) return
    const amount = roundCurrency(Math.abs(toNumber(row.amount)))
    if (amount) {
      const fromKey = normalizeTransferKey(row.from)
      const toKey = normalizeTransferKey(row.to)
      if (fromKey) {
        accumulate(fromKey, -amount)
      }
      if (toKey) {
        accumulate(toKey, amount)
      }
    }
    row.children?.forEach(visit)
  }

  rows.forEach(visit)
  return totals
}

const resolveStatusAmountKey = (
  statusLabel: string | null | undefined,
  amounts: Record<string, number | null> = {}
) => {
  const normalizedStatus = normalizeStatusToken(statusLabel)
  if (normalizedStatus) {
    const direct = Object.keys(amounts).find((key) => normalizeStatusToken(key) === normalizedStatus)
    if (direct) {
      return direct
    }

    const statusKey = normalizePcgVariationStatusKey(statusLabel)
    if (statusKey) {
      const keyMatch = Object.keys(amounts).find(
        (key) => normalizePcgVariationStatusKey(key) === statusKey
      )
      if (keyMatch) {
        return keyMatch
      }
    }
  }

  const nonZero = Object.entries(amounts)
    .filter(([, value]) => toNumber(value) !== 0)
    .map(([key]) => key)

  return nonZero.length === 1 ? nonZero[0] : null
}

const resolveCurrentStatusAmount = (row: VariationLineItem) => {
  const statusKey = resolveStatusAmountKey(row.statusLabel, row.statusAmounts)
  return statusKey ? toNumber(row.statusAmounts?.[statusKey]) : 0
}

const buildVariationThisReportTotalsByFundingSource = (
  rows: VariationLineItem[] = [],
  variationStatusItems: PcgVariationStatusItem[] = []
) => {
  const activeRows = rows.filter(
    (row) => !isRejectedOrWithdrawnPcgVariationRow(row, variationStatusItems)
  )

  if (variationStatusItems.length) {
    const grouped = buildCurrentStatusAmountTotalsByKey(activeRows, variationStatusItems, (row) =>
      normalizeFundingSourceKey(row.fundingSource)
    )
    return new Map(Array.from(grouped.entries()).map(([key, totals]) => [key, totals.total]))
  }

  const totals = new Map<string, number>()

  activeRows
    .filter((row) => !row.isTotalRow)
    .forEach((row) => {
      const key = normalizeFundingSourceKey(row.fundingSource)
      if (!key) {
        return
      }
      totals.set(key, roundCurrency((totals.get(key) ?? 0) + resolveCurrentStatusAmount(row)))
    })

  return totals
}

const resolveFundingSourceVariationTotal = (
  title: string | undefined,
  totalsByFundingSource: Map<string, number>
) => {
  const sourceKey = normalizeFundingSourceKey(title)
  if (!sourceKey) {
    return 0
  }

  const exact = totalsByFundingSource.get(sourceKey)
  if (exact !== undefined) {
    return exact
  }

  const titleTokens = getFundingSourceTokens(sourceKey)
  if (!titleTokens.length) {
    return 0
  }

  const matches = Array.from(totalsByFundingSource.entries()).filter(([candidateKey]) => {
    const candidateTokens = getFundingSourceTokens(candidateKey)
    return (
      candidateTokens.length > 0 &&
      candidateTokens.every((token) => titleTokens.includes(token))
    )
  })

  return matches.length === 1 ? matches[0][1] : 0
}

const calculateContingencyEndCost = (adjustedBudget: number, variationTotal: number) =>
  roundCurrency(Math.max(0, adjustedBudget - variationTotal))

const deriveBudgetFields = (
  item: BudgetLineItem,
  pcgGroup: PcgGroup | null = null,
  variationTotalsByFundingSource: Map<string, number>
) => {
  const adjustedBudget = roundCurrency(
    toNumber(item.initialBudget) + toNumber(item.budgetTransfers)
  )
  const isContingencyRow = pcgGroup === 'contingency' && isEditableRow(item)
  const endCost = isContingencyRow
    ? calculateContingencyEndCost(
        adjustedBudget,
        resolveFundingSourceVariationTotal(item.title, variationTotalsByFundingSource)
      )
    : toNumber(item.endCost)
  const committed = isContingencyRow ? null : item.committed
  const expenditure = isContingencyRow ? null : item.expenditure
  const variance = roundCurrency(endCost - toNumber(item.anticipated))
  const nonCommitted = roundCurrency(endCost - toNumber(committed))
  const costToComplete =
    pcgGroup === 'contingency'
      ? roundCurrency(endCost - toNumber(expenditure))
      : roundCurrency(adjustedBudget - toNumber(expenditure))

  return {adjustedBudget, endCost, committed, expenditure, variance, nonCommitted, costToComplete}
}

const applyBudgetTransferOverrides = (
  rows: BudgetLineItem[],
  totals: Map<string, number>,
  variationTotalsByFundingSource: Map<string, number>,
  currentGroup: PcgGroup | null = null
): BudgetTreeItem[] =>
  rows.map((row) => {
    const resolvedGroup = resolvePcgGroup(row.title)
    const nextGroup = resolvedGroup ?? currentGroup
    const transferValue = (() => {
      if (!isEditableRow(row)) {
        return row.budgetTransfers
      }
      if (nextGroup === 'contingency' || nextGroup === 'other') {
        return roundCurrency(totals.get(normalizeTransferKey(row.title)) ?? 0)
      }
      return 0
    })()
    const withTransfer = {
      ...row,
      budgetTransfers: transferValue,
    }

    return {
      ...withTransfer,
      ...deriveBudgetFields(withTransfer, nextGroup, variationTotalsByFundingSource),
      pcgGroup: nextGroup ?? undefined,
      children: row.children
        ? applyBudgetTransferOverrides(row.children, totals, variationTotalsByFundingSource, nextGroup)
        : undefined,
    }
  })

const collectEditableRows = (
  rows: BudgetTreeItem[],
  predicate: (row: BudgetTreeItem) => boolean
): BudgetTreeItem[] => {
  const collected: BudgetTreeItem[] = []
  const visit = (row: BudgetTreeItem) => {
    if (!row.isExcluded && isEditableRow(row) && predicate(row)) {
      collected.push(row)
    }
    row.children?.forEach(visit)
  }
  rows.forEach(visit)
  return collected
}

const sumFields = (rows: BudgetTreeItem[]): BudgetTotals => {
  const totals = NUMERIC_FIELDS.reduce((acc, field) => {
    acc[field] = 0
    return acc
  }, {} as BudgetTotals)

  NUMERIC_FIELDS.forEach((field) => {
    totals[field] = rows.reduce(
      (acc, row) => acc + toNumber(row[field] as number | null | undefined),
      0
    )
  })
  return totals
}

const isRejectedWithdrawnVariationBudgetRow = (row: BudgetTreeItem) =>
  row.pcgGroup === 'variations' &&
  isEditableRow(row) &&
  isRejectedOrWithdrawnPcgVariationStatus(row.title)

const buildBudgetGroupTotals = (
  budget: BudgetLineItem[],
  budgetTransfers: BudgetTransferLine[] = [],
  variations: VariationLineItem[] = [],
  variationStatusItems: PcgVariationStatusItem[] = []
) => {
  const rows = applyBudgetTransferOverrides(
    budget,
    buildBudgetTransferTotals(budgetTransfers),
    buildVariationThisReportTotalsByFundingSource(variations, variationStatusItems)
  )
  return {
    mainWorks: sumFields(collectEditableRows(rows, (row) => row.pcgGroup === 'mainWorks')),
    variations: sumFields(
      collectEditableRows(
        rows,
        (row) => row.pcgGroup === 'variations' && !isRejectedWithdrawnVariationBudgetRow(row)
      )
    ),
    contingency: sumFields(collectEditableRows(rows, (row) => row.pcgGroup === 'contingency')),
  }
}

const buildMonthRange = (
  startDate?: string | null,
  completionDate?: string | null,
  monthly: CashflowMonthlyLine[] = []
): ContingencyErosionMonth[] => {
  const monthlyDates = monthly
    .filter((row) => !row.isTotalRow)
    .map((row) => parseContingencyMonthValue(row.month))
    .filter((value): value is Date => Boolean(value))
    .sort((a, b) => a.valueOf() - b.valueOf())
  const start = parseContingencyMonthValue(startDate) ?? monthlyDates[0] ?? null
  const end =
    parseContingencyMonthValue(completionDate) ?? monthlyDates[monthlyDates.length - 1] ?? start
  if (!start || !end) {
    return []
  }

  const [rangeStart, rangeEnd] = start <= end ? [start, end] : [end, start]
  const months: ContingencyErosionMonth[] = []
  let cursor = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1)
  const maxMonths = 600
  while (cursor <= rangeEnd && months.length < maxMonths) {
    const label = toMonthLabel(cursor)
    months.push({
      key: `m${months.length}`,
      label,
      date: new Date(cursor),
    })
    cursor = addMonths(cursor, 1)
  }
  return months
}

const buildMonthlyLookup = (monthly: CashflowMonthlyLine[]) => {
  const lookup = new Map<string, CashflowMonthlyLine>()
  monthly
    .filter((row) => !row.isTotalRow)
    .forEach((row) => {
      const label = normalizeMonthLabel(row.month)
      if (label && !lookup.has(label)) {
        lookup.set(label, row)
      }
    })
  return lookup
}

const getMonthlyNumber = (
  lookup: Map<string, CashflowMonthlyLine>,
  label: string,
  field: keyof CashflowMonthlyLine
) => {
  const value = lookup.get(label)?.[field]
  const numeric = Number(value ?? 0)
  return Number.isFinite(numeric) ? numeric : 0
}

const buildSeries = (
  key: ContingencyErosionSeriesKey,
  label: string,
  values: Array<number | null>
): ContingencyErosionSeries => ({
  key,
  label,
  values,
})

const buildRow = (
  id: string,
  metric: string,
  valueType: ContingencyErosionValueType,
  values: Array<number | null>,
  children?: ContingencyErosionTableRow[]
): ContingencyErosionTableRow => ({
  id,
  metric,
  valueType,
  values,
  children,
})

export const buildContingencyErosionData = ({
  budget,
  budgetTransfers = [],
  variations = [],
  variationStatusItems = [],
  monthly,
  contingencyHistory = [],
  constructionStartDate,
  constructionCompletionDate,
  reportDateOfIssue,
}: BuildContingencyErosionDataOptions): ContingencyErosionData => {
  const months = buildMonthRange(constructionStartDate, constructionCompletionDate, monthly)
  const monthlyLookup = buildMonthlyLookup(monthly)
  const groupedTotals = buildBudgetGroupTotals(
    budget,
    budgetTransfers,
    variations,
    variationStatusItems
  )
  const mainWorksAdjustedBudget = groupedTotals.mainWorks.adjustedBudget
  const contingencyAdjustedBudget = groupedTotals.contingency.adjustedBudget
  const contingencyNonCommitted = groupedTotals.contingency.nonCommitted
  const reportMonthLabel = normalizeMonthLabel(reportDateOfIssue)
  const reportMonthDate = parseContingencyMonthValue(reportDateOfIssue)
  const lastActualDataIndex = months.reduce((lastIndex, month, index) => {
    const actualMonthly = getMonthlyNumber(monthlyLookup, month.label, 'actualMonthly')
    return actualMonthly > 0 ? index : lastIndex
  }, -1)
  const contingencyHistoryLookup = new Map<string, PcgContingencyHistoryPoint>()
  contingencyHistory.forEach((point) => {
    const label = normalizeMonthLabel(point.month || point.dateOfIssue)
    if (label && !contingencyHistoryLookup.has(label)) {
      contingencyHistoryLookup.set(label, point)
    }
  })
  const forecastContingencyPercentage = Math.max(
    0,
    dividePercentage(contingencyAdjustedBudget, mainWorksAdjustedBudget) ?? 0
  )
  const forecastContingencyCalculationPercentage =
    roundPercentage(forecastContingencyPercentage) ?? 0
  const forecastContingencyPercentageLabel = formatPercentageLabel(
    forecastContingencyCalculationPercentage
  )
  const forecastContingencyRate = forecastContingencyCalculationPercentage / 100
  const fixedContingencyPercentage = 5
  const fixedContingencyRate = fixedContingencyPercentage / 100

  const forecastExpenditure: number[] = []
  const forecastExpenditurePct: Array<number | null> = []
  const forecastCostToComplete: number[] = []
  const forecastCostToCompletePct: Array<number | null> = []
  const forecastContingencyValue: number[] = []
  const forecastContingencyValuePct: Array<number | null> = []
  const actualExpenditure: Array<number | null> = []
  const actualExpenditurePct: Array<number | null> = []
  const actualCostToComplete: Array<number | null> = []
  const actualCostToCompletePct: Array<number | null> = []
  const contingencyUncommitted: Array<number | null> = []
  const contingencyUncommittedPct: Array<number | null> = []
  const fixedContingencyValue: number[] = []
  const remainingContingencyVsCtcPct: Array<number | null> = []

  const chartActualValue = (value: number | null, index: number) =>
    lastActualDataIndex >= 0 && index <= lastActualDataIndex ? clampChartPercentage(value) : null

  months.forEach((month, index) => {
    const forecast = getMonthlyNumber(monthlyLookup, month.label, 'forecastCumulative')
    const forecastCtc = mainWorksAdjustedBudget - forecast
    const forecastContingency = forecastCtc * forecastContingencyRate
    const fixedContingency = forecastCtc * fixedContingencyRate
    const actual = getMonthlyNumber(monthlyLookup, month.label, 'actualCumulative')
    const actualCtc = mainWorksAdjustedBudget - actual
    const historyPoint = contingencyHistoryLookup.get(month.label)
    const isReportMonth = month.label === reportMonthLabel
    const isFutureReportMonth = reportMonthDate ? month.date > reportMonthDate : false
    const monthlyContingencyAdjusted = isReportMonth
      ? contingencyAdjustedBudget
      : historyPoint?.contingencyAdjustedBudget ?? contingencyAdjustedBudget
    const monthlyContingencyUncommitted = isFutureReportMonth
      ? null
      : isReportMonth
      ? contingencyNonCommitted
      : historyPoint?.contingencyUncommitted ?? null

    forecastExpenditure.push(roundCurrency(forecast))
    forecastExpenditurePct.push(
      roundPercentage(dividePercentage(forecast, mainWorksAdjustedBudget))
    )
    forecastCostToComplete.push(roundCurrency(forecastCtc))
    forecastCostToCompletePct.push(
      roundPercentage(dividePercentage(forecastCtc, mainWorksAdjustedBudget))
    )
    forecastContingencyValue.push(roundCurrency(forecastContingency))
    forecastContingencyValuePct.push(
      roundPercentage(dividePercentage(forecastContingency, contingencyAdjustedBudget))
    )
    fixedContingencyValue.push(roundCurrency(fixedContingency))

    actualExpenditure.push(roundCurrency(actual))
    actualExpenditurePct.push(roundPercentage(dividePercentage(actual, mainWorksAdjustedBudget)))
    actualCostToComplete.push(roundCurrency(actualCtc))
    actualCostToCompletePct.push(
      roundPercentage(dividePercentage(actualCtc, mainWorksAdjustedBudget))
    )
    contingencyUncommitted.push(
      monthlyContingencyUncommitted === null ? null : roundCurrency(monthlyContingencyUncommitted)
    )
    contingencyUncommittedPct.push(
      monthlyContingencyUncommitted === null
        ? null
        : roundPercentage(
            dividePercentage(monthlyContingencyUncommitted, monthlyContingencyAdjusted)
          )
    )
    remainingContingencyVsCtcPct.push(
      monthlyContingencyUncommitted === null
        ? null
        : roundPercentage(dividePercentage(monthlyContingencyUncommitted, actualCtc))
    )
  })

  const tableRows = [
    buildRow(
      'construction-cashflow',
      'Construction Cashflow',
      'currency',
      [],
      [
        buildRow(
          'construction-forecast-expenditure',
          'Forecast Cumulative Cash Flow',
          'currency',
          forecastExpenditure
        ),
        buildRow(
          'construction-actual-expenditure',
          'Actual Cumulative Expenditure',
          'currency',
          actualExpenditure
        ),
      ]
    ),
    buildRow(
      'forecast',
      'Forecast',
      'currency',
      [],
      [
        buildRow('forecast-expenditure', 'Expenditure', 'currency', forecastExpenditure),
        buildRow(
          'forecast-expenditure-pct',
          'Expenditure Percentage',
          'percentage',
          forecastExpenditurePct
        ),
        buildRow('forecast-ctc', 'Cost To Complete', 'currency', forecastCostToComplete),
        buildRow('forecast-ctc-pct', 'CTC Percentage', 'percentage', forecastCostToCompletePct),
        buildRow(
          'forecast-contingency-value',
          `Contingency Value (${forecastContingencyPercentageLabel}%)`,
          'currency',
          forecastContingencyValue
        ),
        buildRow(
          'forecast-contingency-value-pct',
          'Contingency Value Percentage',
          'percentage',
          forecastContingencyValuePct
        ),
      ]
    ),
    buildRow(
      'actual',
      'Actual',
      'currency',
      [],
      [
        buildRow('actual-expenditure', 'Expenditure', 'currency', actualExpenditure),
        buildRow(
          'actual-expenditure-pct',
          'Expenditure Percentage',
          'percentage',
          actualExpenditurePct
        ),
        buildRow('actual-ctc', 'Cost To Complete', 'currency', actualCostToComplete),
        buildRow('actual-ctc-pct', 'CTC Percentage', 'percentage', actualCostToCompletePct),
        buildRow(
          'actual-contingency-uncommitted',
          'Contingency Uncommitted',
          'currency',
          contingencyUncommitted
        ),
        buildRow(
          'actual-contingency-uncommitted-pct',
          'Contingency Uncommitted Percentage',
          'percentage',
          contingencyUncommittedPct
        ),
        buildRow(
          'actual-contingency-value',
          `Contingency Value (${fixedContingencyPercentage}%)`,
          'currency',
          fixedContingencyValue
        ),
      ]
    ),
  ]

  return {
    months,
    tableRows,
    remainingContingencyPercentages: months.map((month, index) => ({
      monthKey: month.key,
      label: month.label,
      value: remainingContingencyVsCtcPct[index] ?? null,
    })),
    expenditureVsContingencySeries: [
      buildSeries(
        'cumulativeForecastExpenditure',
        'Cumulative Forecast Expenditure',
        forecastExpenditurePct.map(clampChartPercentage)
      ),
      buildSeries(
        'actualCostToComplete',
        'Actual Cost To Complete (CTC)',
        actualCostToCompletePct.map(chartActualValue)
      ),
      buildSeries(
        'actualCumulativeExpenditure',
        'Actual Cumulative Expenditure',
        actualExpenditurePct.map(chartActualValue)
      ),
      buildSeries(
        'actualContingencyCostToComplete',
        'Actual Contingency Cost To Complete (CTC)',
        contingencyUncommittedPct.map(chartActualValue)
      ),
      buildSeries(
        'forecastCostToComplete',
        'Forecast Cost To Complete (CTC)',
        forecastCostToCompletePct.map(clampChartPercentage)
      ),
      buildSeries(
        'forecastContingencyErosion',
        'Forecast Contingency Erosion',
        forecastContingencyValuePct.map(clampChartPercentage)
      ),
    ],
    contingencySeries: [
      buildSeries('remainingContingency', 'Remaining Contingency', contingencyUncommitted),
      buildSeries(
        'contingencyForecastPercent',
        `Contingency Forecast ${forecastContingencyPercentageLabel}%`,
        forecastContingencyValue
      ),
      buildSeries(
        'contingencyForecastFivePercent',
        `Contingency Forecast ${fixedContingencyPercentage}%`,
        fixedContingencyValue
      ),
    ],
    totals: {
      mainWorksAdjustedBudget: roundCurrency(mainWorksAdjustedBudget),
      mainWorksActualBaseCostToComplete: roundCurrency(mainWorksAdjustedBudget),
      contingencyAdjustedBudget: roundCurrency(contingencyAdjustedBudget),
      contingencyNonCommitted: roundCurrency(contingencyNonCommitted),
      contingencyForecastPercentage: forecastContingencyCalculationPercentage,
      fixedContingencyPercentage,
    },
  }
}

export const formatContingencyErosionValue = (
  value: number | null | undefined,
  valueType: ContingencyErosionValueType
) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '-'
  }

  if (valueType === 'percentage') {
    return `${Number(value).toFixed(2)} %`
  }

  return Number(value).toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}
